import React from 'react';
import { parseInlineMarkdown } from '../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../utils/dq_lc';
import {
  SlideDescription,
  SlideTitle,
  PointText,
} from '../../../../../styles/LearnTab/slideBase.styles';
import { SlideTitleHeader } from '../common/SlideTitleHeader.comp';
import { renderTextWithId } from '../common/EditableSlideField.comp';

export const ReentryMapSlide = ({
  slide,
  highlightWord,
  slideEdits = {},
  showEnglishContent = false,
  uiLocale = 'en-US',
}) => {
  const origin = slide?._pbdq_origin || slide || {};
  const d = slide?.ReentryData || origin;
  const accent = '#3D5AFE';
  const rawTitle = slide?.SlideTitle || origin.headline || origin.title || '';

  const textRendererOptions = { slideEdits, showEnglishContent, highlightWord };

  if (!d) {
    return (
      <SlideDescription>
        <SlideTitleHeader
          slide={slide}
          slideEdits={slideEdits}
          showEnglishContent={showEnglishContent}
          highlightWord={highlightWord}
        />
        {renderTextWithId('content_0', slide?.SlideContent, PointText, {}, textRendererOptions)}
      </SlideDescription>
    );
  }

  return (
    <SlideDescription>
      {rawTitle ? (
        <SlideTitle>
          {highlightWord ? highlightWord('title', rawTitle) : rawTitle}
        </SlideTitle>
      ) : (
        <SlideTitleHeader
          slide={slide}
          slideEdits={slideEdits}
          showEnglishContent={showEnglishContent}
          highlightWord={highlightWord}
        />
      )}

      {d.one_line_summary && (
        <div style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.6, marginBottom: 16 }}>
          {renderTextWithId('reentry_summary', d.one_line_summary, 'span', {}, textRendererOptions)}
        </div>
      )}

      {(d.walked_in_with || d.walked_out_with) ? (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '12px 0 18px' }}>
          {d.walked_in_with && (
            <div style={{ flex: '1 1 240px', background: 'rgba(0,0,0,0.03)', padding: '14px 18px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', marginBottom: 6 }}>
                {tUi('slide.reentry.walkedInWith', uiLocale)}
              </div>
              <div style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.5 }}>{renderTextWithId('reentry_in', d.walked_in_with, 'span', {}, textRendererOptions)}</div>
            </div>
          )}
          {d.walked_out_with && (
            <div style={{ flex: '1 1 240px', background: `${accent}0D`, padding: '14px 18px', borderRadius: 8, border: `1px solid ${accent}26` }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', marginBottom: 6 }}>
                {tUi('slide.reentry.walkedOutWith', uiLocale)}
              </div>
              <div style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.5 }}>{renderTextWithId('reentry_out', d.walked_out_with, 'span', {}, textRendererOptions)}</div>
            </div>
          )}
        </div>
      ) : d.framing_paragraph ? (
        <div style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.6, marginBottom: 16 }}>
          {renderTextWithId('reentry_framing', d.framing_paragraph, 'span', {}, textRendererOptions)}
        </div>
      ) : null}

      {d.practice_intro ? (
        <div style={{ margin: '14px 0 8px', fontSize: '1rem', color: '#374151', lineHeight: 1.5 }}>
          {renderTextWithId('reentry_practice_intro', d.practice_intro, 'span', {}, textRendererOptions)}
        </div>
      ) : d.practice_timeframe ? (
        <div style={{ margin: '14px 0 8px', fontSize: '1rem', color: '#374151', lineHeight: 1.5 }}>
          To put this into practice during <strong>{d.practice_timeframe}</strong>, use this quick-reference guide:
        </div>
      ) : null}

      {Array.isArray(d.reentry_map) && d.reentry_map.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 14px', borderBottom: `2px solid ${accent}`, width: '50%', fontWeight: 700, fontSize: '0.95rem' }}>
                {tUi('slide.reentry.situation', uiLocale)}
              </th>
              <th style={{ textAlign: 'left', padding: '10px 14px', borderBottom: `2px solid ${accent}`, fontWeight: 700, fontSize: '0.95rem' }}>
                {tUi('slide.reentry.goBackTo', uiLocale)}
              </th>
            </tr>
          </thead>
          <tbody>
            {d.reentry_map.map((row, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(0,0,0,0.08)', verticalAlign: 'top', fontSize: '1rem', color: '#374151' }}>
                  {row.workplace_moment || row.moment}
                </td>
                <td style={{ textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(0,0,0,0.08)', verticalAlign: 'top', fontSize: '1rem', color: '#374151' }}>
                  {row.return_section || row.section}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {d.closing_line && (
        <div className="content-so-what" style={{ marginTop: 16 }}>
          {parseInlineMarkdown(d.closing_line)}
        </div>
      )}

      {d.t_plus_30_message && (
        <PointText style={{ marginTop: 14 }}>
          <strong>Follow-up:</strong> {renderTextWithId('reentry_t30', d.t_plus_30_message, 'span', {}, textRendererOptions)}
        </PointText>
      )}
    </SlideDescription>
  );
};

export default ReentryMapSlide;
